import { Fragment } from 'react';
import { TechnicianInfo } from '../../../entities/user';
import { TECHNICIAN_COL_WIDTH, WEEK_DAY_COUNT } from '../../../shared/lib/schedule';
import { BOARD_LAYER } from '../model/boardLayers';
import type { DispositionWeek } from '../model/useDispositionWeek';
import { useDispositionWeekBoard } from '../model/useDispositionWeekBoard';
import { WeekCell } from './WeekCell';
import { WeekDayHeader } from './WeekDayHeader';

/**
 * The schedule payload carries no roles, and every technician on this board is a Handwerker —
 * which is exactly what `getPositionsFromRoles` renders for `WORKER`. Same reasoning as `BoardRow`.
 */
const TECHNICIAN_ROLES = ['WORKER'] as const;

/** Narrow enough that five columns plus the technician column fit a laptop without scrolling. */
const MIN_DAY_COLUMN_WIDTH = 180;

interface Props {
  week: DispositionWeek;
}

/**
 * The week board — technicians down the left, Montag … Freitag across the top.
 *
 * **Read-only by construction.** There is no time axis, so there is nothing to drag along and none
 * of the day board's geometry, drag primitive or planning machinery is reachable from here. A click
 * anywhere takes the user to the day board, which is where a block can actually be moved.
 *
 * Plain CSS grid rather than the day board's absolutely-positioned blocks: with no axis, a row's
 * height is simply its busiest cell, which grid gives for free.
 */
export const DispositionWeekWidget = ({ week }: Props) => {
  const { rows, isLoading, isError, refetch } = useDispositionWeekBoard(
    week.days,
    week.from,
    week.to,
    week.todayIso,
  );

  const gridTemplateColumns = `${TECHNICIAN_COL_WIDTH}px repeat(${WEEK_DAY_COUNT}, minmax(${MIN_DAY_COLUMN_WIDTH}px, 1fr))`;

  return (
    <div className="h-full overflow-auto bg-white">
      <div
        className="grid"
        style={{
          gridTemplateColumns,
          // Below this the columns would squeeze an address down to one word, so the grid scrolls
          // sideways instead; above it the `1fr` columns share the extra width.
          minWidth: TECHNICIAN_COL_WIDTH + WEEK_DAY_COUNT * MIN_DAY_COLUMN_WIDTH,
        }}
      >
        {/* Header — sticky in both axes at the corner, so neither scroll direction loses it. */}
        <div
          className={`sticky left-0 shrink-0 bg-white px-3 py-3 text-base text-zinc-500 outline outline-1 outline-offset-[-0.50px] outline-slate-200 ${BOARD_LAYER.headerCorner}`}
        >
          Handwerkern
        </div>
        {week.days.map((dayIso) => (
          <div key={dayIso} className={`sticky top-0 ${BOARD_LAYER.header}`}>
            <WeekDayHeader
              dayIso={dayIso}
              isToday={dayIso === week.todayIso}
              onOpenDay={week.openDay}
            />
          </div>
        ))}

        {rows.map((row) => (
          <Fragment key={row.technicianId}>
            <div className={`sticky left-0 flex bg-white ${BOARD_LAYER.technicianColumn}`}>
              <TechnicianInfo
                technicianId={String(row.technicianId)}
                technicianName={row.technicianName}
                technicianRoles={[...TECHNICIAN_ROLES]}
                technicianAvatar={row.technicianAvatar}
              />
            </div>

            {row.cells.map((cell) => (
              <WeekCell
                key={cell.dayIso}
                cell={cell}
                technicianId={row.technicianId}
                onOpenDay={week.openDay}
              />
            ))}
          </Fragment>
        ))}
      </div>

      {isLoading && <WeekSkeleton />}

      {!isLoading && isError && (
        <WeekMessage
          text="Der Wochenplan konnte nicht geladen werden."
          action={{ label: 'Erneut versuchen', onClick: refetch }}
        />
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <WeekMessage text="Keine Handwerker für diese Woche." />
      )}
    </div>
  );
};

const SKELETON_ROWS = 6;

/** Placeholder rows while the schedule query is in flight — one per expected technician. */
const WeekSkeleton = () => (
  <div className="animate-pulse">
    {Array.from({ length: SKELETON_ROWS }, (_, i) => (
      <div key={i} className="flex h-[92px] border-b border-slate-200">
        <div
          className="flex shrink-0 items-center gap-3.5 border-r border-slate-200 p-3"
          style={{ width: TECHNICIAN_COL_WIDTH }}
        >
          <div className="h-11 w-11 shrink-0 rounded-full bg-gray-200" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-28 rounded bg-gray-200" />
            <div className="h-3 w-20 rounded bg-gray-100" />
          </div>
        </div>
        <div className="flex-1" />
      </div>
    ))}
  </div>
);

const WeekMessage = ({
  text,
  action,
}: {
  text: string;
  action?: { label: string; onClick: () => void };
}) => (
  <div className="flex flex-col items-center gap-3 py-16 text-sm text-gray-400">
    <span>{text}</span>
    {action && (
      <button
        type="button"
        onClick={action.onClick}
        className="rounded border border-slate-200 px-3 py-1.5 text-[#124093] hover:bg-gray-50"
      >
        {action.label}
      </button>
    )}
  </div>
);
